import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchUsage, type CreditBalance, type CreditSpend, type Usage } from '../../../api/billingApi';
import styles from './UsageMeters.module.css';

/** This month's metered usage: AI queries, document analyses, active alerts — with the plan's limit and reset date. */
export function useUsage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  useEffect(() => { fetchUsage().then(setUsage).catch(() => setUsage(null)); }, []);
  return usage;
}

export const fmtLimit = (m: { used: number; limit: number | null }) => (m.limit === null ? `${m.used} · unlimited` : `${m.used} / ${m.limit}`);

/** Credits are shown only once the plan carries a figure. Every catalogue row
 *  predates the allowance and so reads 0, which would tell a paying customer
 *  they had no credits at all — the server says ``enforced: false`` for that
 *  case and the meter stays hidden until an administrator sets one. */
export const showCredits = (c?: CreditBalance): c is CreditBalance => !!c && (c.enforced || c.unlimited);

/** What the credits went on. A customer could previously see only the total,
 *  so a falling balance had no explanation — and since agent runs, assessments
 *  and reports began drawing on the same pool there are several reasons at
 *  once. Rows with no credits are dropped: an action that costs nothing is not
 *  an explanation of where the credits went. */
export const spendRows = (c?: CreditBalance): CreditSpend[] =>
  (c?.by_action || []).filter(r => r.credits > 0);

export const fmtSpend = (r: CreditSpend): string =>
  `${r.credits.toLocaleString()} · ${r.times.toLocaleString()}\u00d7`;

export const fmtCredits = (c: CreditBalance) =>
  c.unlimited ? `${c.spent_this_period.toLocaleString()} · unlimited` : `${c.allowance_used.toLocaleString()} / ${(c.allowance ?? 0).toLocaleString()}`;

const UsageMeters: React.FC<{ usage: Usage | null }> = ({ usage }) => {
  if (!usage) return null;
  const resets = new Date(usage.resets_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return (
    <div className={styles.wrap} data-testid="usage-meters">
      <div className={styles.head}>
        <span className={styles.title}>Usage this month</span>
        <span className={styles.sub}>Monthly allowances reset on {resets}</span>
      </div>
      <div className={styles.grid}>
        {showCredits(usage.credits) && (() => {
          const c = usage.credits as CreditBalance;
          const pct = c.pct ?? 0;
          const spent = !c.unlimited && (c.allowance_remaining ?? 0) <= 0;
          const low = !c.unlimited && !spent && pct >= 80;
          return (
            <div className={styles.meter} data-testid="meter-credits">
              <div className={styles.row}>
                <span className={styles.label}>AI credits</span>
                <span className={`${styles.value} ${spent ? styles.exhausted : low ? styles.warning : ''}`}>{fmtCredits(c)}</span>
              </div>
              <div className={styles.bar} role="progressbar" aria-label="AI credits" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                <div className={`${styles.fill} ${spent ? styles.fillExhausted : low ? styles.fillWarning : ''}`} style={{ width: `${c.unlimited ? 0 : pct}%` }} />
              </div>
              <div className={styles.hint}>
                {c.unlimited ? 'No credit limit on your plan' : <>
                  {spent
                    ? (c.purchased_remaining > 0
                        // "bought" would be wrong: credits cannot be bought. The
                        // purchased pool only ever fills from an admin grant.
                        ? `Monthly allowance spent · using your ${c.purchased_remaining.toLocaleString()} extra credits`
                        : <>No credits left · <Link to="/pricing">upgrade for more</Link></>)
                    : `${(c.allowance_remaining ?? 0).toLocaleString()} left this month`}
                  {c.purchased_remaining > 0 && !spent && ` · plus ${c.purchased_remaining.toLocaleString()} extra`}
                </>}
              </div>
              {spendRows(c).length > 0 && (
                <div className={styles.breakdown} data-testid="credit-breakdown">
                  <span className={styles.breakdownHead}>Where they went</span>
                  {spendRows(c).map(r => (
                    <div key={r.action} className={styles.spend}>
                      <span className={styles.spendLabel}>{r.label}</span>
                      <span className={styles.spendValue}>{fmtSpend(r)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        {usage.meters.map(m => (
          <div key={m.meter} className={styles.meter} data-testid={`meter-${m.meter}`}>
            <div className={styles.row}>
              <span className={styles.label}>{m.label}</span>
              <span className={`${styles.value} ${m.exhausted ? styles.exhausted : m.warning ? styles.warning : ''}`}>{fmtLimit(m)}</span>
            </div>
            <div className={styles.bar} role="progressbar" aria-label={m.label} aria-valuenow={m.pct ?? 0} aria-valuemin={0} aria-valuemax={100}>
              <div className={`${styles.fill} ${m.exhausted ? styles.fillExhausted : m.warning ? styles.fillWarning : ''}`} style={{ width: `${m.limit === null ? 0 : m.pct ?? 0}%` }} />
            </div>
            <div className={styles.hint}>
              {m.limit === null ? 'No limit on your plan' : m.exhausted ? <>Limit reached · <Link to="/pricing">upgrade for more</Link></> : m.warning ? `${m.remaining} left${m.monthly ? ' this month' : ''}` : m.monthly ? 'Counts each successful request' : 'Active alerts count against the ceiling'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsageMeters;
